import { answerAsString, DIGITAL_SIGNATURE_CONSENT_MARKER } from "./answerFormat";
import { isImageSource } from "./blockFormat";

// Pure value logic for the signature editors (employeeSignature, patientSignature,
// customSignature). Mirrors how mobile (RequiredDocSignature.getAnswerData) and the classic modal
// (editPatientDocModalPatientSignatureController / editPatientDocModalSignatureController) read a
// stored signature answer: the consent marker first, then an image, then any other text means the
// signee did not sign (a reason, the "Other" text, or the name an employee typed).

export { DIGITAL_SIGNATURE_CONSENT_MARKER };

/** The stored reasons; "Other" is not stored, its free text is. */
export const PATIENT_NOT_SIGNING_REASONS = [
  "Patient unable to sign",
  "Patient refused to sign",
  "Verbal consent given by patient/family",
] as const;

export const PATIENT_NOT_SIGNING_OTHER = "Other";

export type PatientNotSigningReason = typeof PATIENT_NOT_SIGNING_REASONS[number];

export type SignatureValue =
  | { kind: "empty" }
  | { kind: "image"; dataUrl: string }
  | { kind: "consent" }
  | { kind: "notSigning"; text: string };

export function classifySignatureAnswer(answer: unknown): SignatureValue {
  const value = answerAsString(answer) ?? "";
  if (value === "") {
    return { kind: "empty" };
  }
  if (value === DIGITAL_SIGNATURE_CONSENT_MARKER) {
    return { kind: "consent" };
  }
  // Same test as the preview (resolveSignature), so the editor and the view always agree.
  if (isImageSource(value)) {
    return { kind: "image", dataUrl: value };
  }
  return { kind: "notSigning", text: value };
}

function isPatientNotSigningReason(text: string): text is PatientNotSigningReason {
  return (PATIENT_NOT_SIGNING_REASONS as readonly string[]).includes(text);
}

/** Which radio is selected for a stored not-signing text (mobile getAnswerData). */
export function patientNotSigningSelection(text: string): {
  reason: PatientNotSigningReason | typeof PATIENT_NOT_SIGNING_OTHER;
  otherText: string;
} {
  if (isPatientNotSigningReason(text)) {
    return { reason: text, otherText: "" };
  }
  return { reason: PATIENT_NOT_SIGNING_OTHER, otherText: text };
}
